/**
 * Report List Component
 * Thin component - all logic delegated to useReportList hook
 */
import { useNavigate } from 'react-router-dom'
import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    FilterBar,
} from '@/components/ui/data-table-customized'
import SubHeaders from '@/components/Headers/SubHeaders'
import AddIcons from '@/icons/AddIcons'
import { Activity } from 'lucide-react'
import type { ReportWAF } from '../../types/response'
import { useReportList } from '../../hooks/use-report-list'
import ResendReportDialog from './ResendReportDialog'
import ViewReportDialog from './ViewReportDialog'

const ReportList = () => {
    const navigate = useNavigate()

    const {
        reports,
        pagination,
        stateComponent,
        params,
        control,
        setFilter,
        searchInputProps,
        filterItems,
        columns,
        actions,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        resendDialog,
        setResendDialog,
        viewDialog,
        setViewDialog,
        doResend,
        isResending,
        doView,
        isViewing,
        t,
    } = useReportList()

    return (
        <>
            <SubHeaders
                breadcrumbs={[
                    { path: '/technical', title: 'Technical' },
                    { path: '/technical/report', title: t('list_title') },
                ]}
                actions={[
                    {
                        label: t('pipeline.view_dashboard', {
                            defaultValue: 'Pipeline',
                        }),
                        icon: <Activity className="w-4 h-4" />,
                        onClick: () => navigate('/technical/report/pipeline'),
                    },
                    {
                        label: t('buttons.create'),
                        icon: <AddIcons className="w-4 h-4" />,
                        onClick: () => navigate('/technical/report/new'),
                    },
                ]}
            />
            <Paper className="space-y-2 my-2">
                <FilterBar
                    control={control}
                    setFilter={setFilter}
                    items={filterItems}
                />
                {stateComponent || (
                    <DataTableCustomized<ReportWAF>
                        columns={columns}
                        rows={reports}
                        rowKey={(row) => String(row.id)}
                        size="small"
                        toolbar={{
                            search: {
                                ...searchInputProps,
                                placeholder: t('filters.search_placeholder'),
                            },
                        }}
                        actions={actions}
                        pagination={{
                            page: params.page,
                            pageSize: params.page_size,
                            total: pagination?.total || 0,
                            onPageChange: handlePageChange,
                            onPageSizeChange: handlePageSizeChange,
                            pageSizeOptions: [10, 20, 50, 100],
                        }}
                        sorting={{
                            sortBy: params.sort_by,
                            sortOrder: params.sort_order,
                            onSortChange: handleSortChange,
                        }}
                    />
                )}
            </Paper>

            <ResendReportDialog
                open={resendDialog[0]}
                reportId={resendDialog[1]}
                onClose={() => setResendDialog([false, ''])}
                onConfirm={doResend}
                isLoading={isResending}
            />
            <ViewReportDialog
                open={viewDialog[0]}
                reportId={viewDialog[1]}
                onConfirm={doView}
                onClose={() => setViewDialog([false, ''])}
                isLoading={isViewing}
            />
        </>
    )
}

export default ReportList
